import React, { useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Paper,
    styled,
    LinearProgress,
    IconButton,
    Select,
    InputLabel,
    FormControl,
    MenuItem,
    Divider,
    InputAdornment,
    Tooltip,
    CircularProgress,
} from '@mui/material';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import { CloudUpload, Save, RestartAlt, Place, Image as ImageIcon } from '@mui/icons-material';
import { AdminPage, SectionTitle, GREEN, LIGHT, BORDER, primaryButtonSx, subCardSx } from '../admin/AdminPageLayout';

// สร้าง input component ที่ซ่อนอยู่
const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// ข้อมูลอำเภอจำลอง
const districts = [
    { value: '1001', label: 'เมือง' },
    { value: '1002', label: 'บางใหญ่' },
    { value: '1003', label: 'สามพราน' },
    { value: '1004', label: 'ลำลูกกา' },
    { value: '1005', label: 'หาดใหญ่' },
];


function MainEditImageDetailsPlace() {
    // image
    const [imageFile, setImageFile] = useState(null);
    const [fileName, setFileName] = useState('');
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [selectedDistrictImg, setSelectedDistrictImg] = useState(''); // State เลือกอำเภอสำหรับรูปภาพ

    // ----------------- IMG ------------------ //
    const handleFileChangeImg = (event) => {
        const file = event.target.files[0];
        if (file) {
            setImageFile(file);
            setFileName(file.name);
            // รีเซ็ตสถานะการอัปโหลด
            setProgress(0);
            setUploading(false);
        }
    };

    const handleUploadImg = () => {
        if (!imageFile) return;

        setUploading(true);
        // จำลองการอัปโหลดไฟล์
        const interval = setInterval(() => {
            setProgress((oldProgress) => {
                if (oldProgress === 100) {
                    clearInterval(interval);
                    setUploading(false);
                    return 100;
                }
                const diff = Math.random() * 15;
                return Math.min(oldProgress + diff, 100);
            });
        }, 500);
    };

    const handleRemoveImg = () => {
        setImageFile(null);
        setFileName('');
        setProgress(0);
        setUploading(false);
    };
    // Handler สำหรับ Image
    const handleDistrictChangeImg = (event) => {
        setSelectedDistrictImg(event.target.value);
    };

    return (
        <AdminPage
            icon={<PhotoCamera fontSize="large" />}
            title="แก้ไขรูปภาพสไลด์ของเส้นทาง"
            subtitle="เลือกเส้นทาง แล้วอัปโหลดรูปภาพใหม่เพื่อแทนที่"
            actions={
                <>
                    {/* ปุ่มยกเลิก */}
                    <Button
                        variant="outlined"
                        color="inherit"
                        startIcon={<RestartAlt />}
                        onClick={handleRemoveImg}
                        disabled={uploading}
                        sx={{ borderRadius: 3, width: { xs: "100%", sm: "auto" }, color: "text.secondary", borderColor: "#ccc" }}
                    >
                        ยกเลิก
                    </Button>
                    {/* ปุ่มบันทึก */}
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <Save />}
                        onClick={handleUploadImg}
                        disabled={!imageFile || uploading}
                        sx={primaryButtonSx}
                    >
                        {uploading ? 'กำลังบันทึก...' : 'บันทึก'}
                    </Button>
                </>
            }
        >
            {/* ① เลือกเส้นทาง */}
            <SectionTitle step={1} title="เลือกเส้นทาง" />
            <FormControl fullWidth disabled={uploading}>
                <InputLabel id="district-select-img-label">เลือกเส้นทาง</InputLabel>
                <Select
                    labelId="district-select-img-label"
                    id="district-select-img"
                    value={selectedDistrictImg}
                    label="เลือกเส้นทาง"
                    onChange={handleDistrictChangeImg}
                    color="primary"
                    startAdornment={
                        <InputAdornment position="start">
                            <Place sx={{ color: GREEN }} />
                        </InputAdornment>
                    }
                    sx={{ borderRadius: 3 }}
                >
                    {districts?.map((district) => (
                        <MenuItem key={district.value} value={district.value}>
                            {district.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            {/* ② อัปโหลดรูปใหม่ */}
            <SectionTitle step={2} title="อัปโหลดรูปภาพใหม่" />
            <Box
                sx={{
                    border: `2px dashed ${BORDER}`,
                    borderRadius: 3,
                    bgcolor: LIGHT,
                    p: 3,
                    textAlign: "center",
                }}
            >
                <CloudUpload sx={{ fontSize: 48, color: GREEN }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    เลือกไฟล์รูปภาพ 1 ไฟล์
                </Typography>
                <Button
                    component="label"
                    role={undefined}
                    variant="outlined"
                    tabIndex={-1}
                    startIcon={<CloudUpload />}
                    sx={{ borderRadius: 3, color: GREEN, borderColor: GREEN, bgcolor: "white" }}
                    disabled={uploading}
                >
                    อัปโหลดรูปภาพใหม่
                    <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChangeImg} />
                </Button>
            </Box>

            {/* แสดงชื่อไฟล์และปุ่มลบ */}
            {fileName ? (
                <Paper variant="outlined" sx={{ ...subCardSx, mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ImageIcon sx={{ color: GREEN }} />
                    <Typography variant="body2" noWrap sx={{ flexGrow: 1, color: 'text.secondary' }}>
                        ไฟล์ที่เลือก: <b>{fileName}</b>
                    </Typography>
                    <Tooltip title="ลบไฟล์">
                        <span>
                            <IconButton
                                aria-label="delete"
                                onClick={handleRemoveImg}
                                disabled={uploading}
                                color="error"
                            >
                                <DeleteIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Paper>
            ) : (
                <Box sx={{ textAlign: "center", py: 3, color: "text.disabled" }}>
                    <ImageIcon sx={{ fontSize: 40 }} />
                    <Typography variant="body2">ยังไม่ได้เลือกรูปภาพ</Typography>
                </Box>
            )}

            {/* Progress Bar ขณะอัปโหลด */}
            {uploading && (
                <Box sx={{ width: '100%', mt: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 4, bgcolor: LIGHT, "& .MuiLinearProgress-bar": { bgcolor: GREEN } }}
                    />
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                        กำลังอัปโหลด... {Math.round(progress)}%
                    </Typography>
                </Box>
            )}
        </AdminPage>
    )
}

export default MainEditImageDetailsPlace